import type { SyntaxNode } from 'web-tree-sitter';
import type { SymbolImportBinding, SymbolImportKind } from '../../types.js';
import { pushBinding, stringLiteralText } from './shared.js';

function importKindForSpecifier(specifier: SyntaxNode, statementKind: SymbolImportKind): SymbolImportKind {
  if (specifier.children.some((child) => child.type === 'type')) return 'type';
  return statementKind;
}

function collectFromImportSpecifier(
  out: SymbolImportBinding[],
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
  moduleSpecifier: string,
  importKind: SymbolImportKind,
  specifier: SyntaxNode,
): void {
  const nameNode = specifier.childForFieldName('name');
  const aliasNode = specifier.childForFieldName('alias');
  if (!nameNode) return;
  const sourceName = nameNode.text;
  const localName = aliasNode?.text ?? sourceName;
  const effectiveKind = importKindForSpecifier(specifier, importKind);
  pushBinding(out, filePath, workspaceFiles, moduleSpecifier, localName, sourceName, effectiveKind, aliasNode ?? nameNode);
}

function collectFromImportClause(
  out: SymbolImportBinding[],
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
  moduleSpecifier: string,
  importKind: SymbolImportKind,
  clause: SyntaxNode,
): void {
  const defaultName = clause.childForFieldName('name');
  if (defaultName) {
    pushBinding(out, filePath, workspaceFiles, moduleSpecifier, defaultName.text, defaultName.text, 'default', defaultName);
  }

  for (const child of clause.children) {
    if (child.type === 'named_imports') {
      for (const spec of child.children) {
        if (spec.type === 'import_specifier') {
          collectFromImportSpecifier(out, filePath, workspaceFiles, moduleSpecifier, importKind, spec);
        }
      }
    }
    if (child.type === 'import_specifier') {
      collectFromImportSpecifier(out, filePath, workspaceFiles, moduleSpecifier, importKind, child);
    }
    if (child.type === 'namespace_import') {
      const alias = child.childForFieldName('name') ?? child.namedChildren.find((n) => n.type === 'identifier');
      if (alias) {
        pushBinding(out, filePath, workspaceFiles, moduleSpecifier, alias.text, alias.text, 'namespace', alias);
      }
    }
  }
}

function moduleSpecifierFromImport(node: SyntaxNode): string | null {
  const source = node.childForFieldName('source');
  if (!source) return null;
  return stringLiteralText(source);
}

export function collectFromImportStatement(
  out: SymbolImportBinding[],
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
  node: SyntaxNode,
): void {
  const moduleSpecifier = moduleSpecifierFromImport(node);
  if (!moduleSpecifier) return;

  const isTypeImport = node.children.some((child) => child.type === 'type');
  const importKind: SymbolImportKind = isTypeImport ? 'type' : 'named';

  const clause = node.children.find((child) => child.type === 'import_clause');
  if (clause) {
    collectFromImportClause(out, filePath, workspaceFiles, moduleSpecifier, importKind, clause);
  }
}

export function extractEcmaScriptImportBindings(
  root: SyntaxNode,
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
): SymbolImportBinding[] {
  const out: SymbolImportBinding[] = [];
  for (const node of root.descendantsOfType('import_statement')) {
    collectFromImportStatement(out, filePath, workspaceFiles, node);
  }
  return out;
}
